import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import EntityCard from "@/components/EntityCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { Filter } from "lucide-react";

const PRICED_TYPES = ["hotel", "dharamshala", "package", "yatra"];
const SKELETONS = ["s1", "s2", "s3", "s4", "s5", "s6"];

export default function Listing({ type, title }) {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [state, setStateF] = useState(params.get("state") || "");
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const [maxPrice, setMaxPrice] = useState(Number(params.get("max_price")) || 200000);
  const [minRating, setMinRating] = useState(Number(params.get("min_rating")) || 0);

  useEffect(() => {
    setItems(null);
    const query = { type, sort };
    if (state) query.state = state;
    if (maxPrice < 200000) query.max_price = maxPrice;
    if (minRating > 0) query.min_rating = minRating;
    api.get("/entities", { params: query }).then(r => setItems(r.data)).catch(() => setItems([]));
    const p = new URLSearchParams();
    if (state) p.set("state", state);
    if (sort !== "newest") p.set("sort", sort);
    setParams(p, { replace: true });
  }, [type, state, sort, maxPrice, minRating, setParams]);

  const hasPrice = PRICED_TYPES.includes(type);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gold-dark">Browse</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-indigo-deep">{title}</h1>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* Filters */}
        <aside className="bg-white rounded-xl p-5 card-shadow border border-gold/10 h-fit lg:sticky lg:top-20">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-gold-dark" />
            <span className="font-semibold text-indigo-deep">Filters</span>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-indigo-deep/60">State</label>
              <Select value={state || "all"} onValueChange={(v) => setStateF(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-state" className="mt-1"><SelectValue placeholder="All states" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All states</SelectItem>
                  <SelectItem value="Uttarakhand">Uttarakhand</SelectItem>
                  <SelectItem value="Uttar Pradesh">Uttar Pradesh</SelectItem>
                  <SelectItem value="Jammu & Kashmir">Jammu & Kashmir</SelectItem>
                  <SelectItem value="Punjab">Punjab</SelectItem>
                  <SelectItem value="Gujarat">Gujarat</SelectItem>
                  <SelectItem value="Andhra Pradesh">Andhra Pradesh</SelectItem>
                  <SelectItem value="Multi-state">Multi-state</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-indigo-deep/60">Sort by</label>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="filter-sort" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  {hasPrice && <SelectItem value="price_asc">Price: low to high</SelectItem>}
                  {hasPrice && <SelectItem value="price_desc">Price: high to low</SelectItem>}
                  <SelectItem value="rating_desc">Top rated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasPrice && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-indigo-deep/60">
                  Max price: ₹{maxPrice.toLocaleString("en-IN")}
                </label>
                <Slider data-testid="filter-price" value={[maxPrice]} onValueChange={(v) => setMaxPrice(v[0])} min={500} max={200000} step={500} className="mt-3" />
              </div>
            )}

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-indigo-deep/60">Min rating: {minRating || "Any"}</label>
              <Slider data-testid="filter-rating" value={[minRating]} onValueChange={(v) => setMinRating(v[0])} min={0} max={5} step={0.5} className="mt-3" />
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div>
          {items === null ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {SKELETONS.map((k) => <Skeleton key={k} className="h-80 rounded-xl" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 text-indigo-deep/60">
              <div className="font-display text-2xl mb-2">No results found</div>
              <p className="text-sm">Try adjusting filters or exploring other categories.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="listing-grid">
              {items.map(e => <EntityCard key={e.id} e={e} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
